import React, { useState, useEffect } from "react";
import { Box, Button, Grid, Modal, Typography } from "@mui/material";
import { useFormik } from "formik";
import TextFieldInput from "../../../components/TextFieldInput";
import AddIcon from "../../../assets/icons/Icon.svg";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import "./style.scss";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/Loader";
import { useParams } from "react-router-dom";
import { PRIMARY_SOURCE } from "../../../config/constant";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import { 
    setActiveStep, 
    setShowReferralModal,
    createReferrals,
    getApplicantDetails,
    getReferralDetails,
    setReferralList,
} from "../new-booking-form.slice";
import { RHFPhoneInput } from "../../../shared-components/phone-input/RHFPhoneInput";
import { isValidPhoneNumberWithRules } from "../../../utilis/common-funtions";
import { useTranslation } from "react-i18next";

const ReferralDetails = () => {
  const [snackbarConfig, setSnackBarconfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  
  const dispatch = useDispatch();
  const { oppId } = useParams();
  const { t } = useTranslation();
  const {
    enquiryDetails,
    isLoading,
    activeStep,
    showReferralModal,
    referralDetails,
    applicantData
  } = useSelector((state) => state?.newBookingForm);

  const getBrandName = () => {
    if (
      enquiryDetails?.data &&
      enquiryDetails?.data?.projectBrandName === "Puravankara"
    ) {
      return "Puravankara Project";
    } else if (
      enquiryDetails?.data &&
      enquiryDetails?.data?.projectBrandName === "Provident"
    ) {
      return "Provident Project";
    } else if (
      enquiryDetails?.data &&
      enquiryDetails?.data?.projectBrandName === "Purva Land"
    ) {
      return "Purva Land";
    }
  };

  const brandName =
    enquiryDetails?.data?.projectBrandName === "Provident"
      ? PRIMARY_SOURCE.ProvidentPremiere
      : PRIMARY_SOURCE.PurvaPrivilege;

  const brandShortName =
    enquiryDetails?.data?.projectBrandName === "Provident"
      ? "Provident"
      : "Purva";

  const userName =
    applicantData?.applicant1.personalDetails.firstName +
      applicantData?.applicant1.personalDetails.lastName ||
    enquiryDetails?.data?.Cname;

  // Validation schema
  const validationSchema = yup.object().shape({
    referralList: yup.array().of(
      yup.object().shape({
        fullName: yup.string().required(t("referralDetails.nameRequired")),
        email: yup
          .string()
          .matches(
            /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
            t("commonValidations.emailAddressInvalid")
          ),
        mobileNumber: yup
          .string()
          .required(t("commonValidations.contactNumber"))
          .test(
            "is-valid-phone",
            t("commonValidations.contactNumberInvalid"),
            (value, context) => {
              const { mobileNumber, countryCode } = context.parent;
              return isValidPhoneNumberWithRules(countryCode, mobileNumber);
            }
          )
          .test(
            "is-unique-phone",
            t("referralDetails.contactNumberUnique"),
            function (value, context) {
              const { referralList } = context.options.context;
              if (!value || !referralList) return true;

              const duplicateCount = referralList.filter(
                (item) => item.mobileNumber === value
              ).length;

              return duplicateCount === 1;
            }
          ),
      })
    ),
  });
  const ReferralDetailFormik = useFormik({
    initialValues: {
      referralList: [
        {
          fullName: "",
          email: "",
          countryCode: "IN",
          mobileNumber: "",
          opportunityId: oppId || "",
          primarySource:
            enquiryDetails?.data?.projectBrandName === "Provident"
              ? PRIMARY_SOURCE?.ProvidentPremiere
              : PRIMARY_SOURCE?.PurvaPrivilege,
          secondarySource: "Referral at booking",
          projectName: enquiryDetails?.data?.ProjectName || "",
          referredApartment: getBrandName() || "",
          projectCity: enquiryDetails?.data?.projectCity || "",
        },
      ],
    },
    validationSchema,
  });

  const handleSaveForm = () => {
    let payload = {
      referrals: ReferralDetailFormik?.values?.referralList,
    };
    dispatch(createReferrals(payload))
      .unwrap()
      .then((res) => {
        if (res?.data && res?.data?.length > 0) {
          dispatch(setReferralList(res?.data || []));
        } else {
          dispatch(setReferralList([]));
        }
        dispatch(setShowReferralModal(false));
        dispatch(setActiveStep(activeStep + 1));
        dispatch(getApplicantDetails(`/${oppId}`));
      })
      .catch((error) => {
        let errorMessage = "An error occurred while saving the form."
        if (error.errors.message) {
          errorMessage = error.errors.message
        }
        setSnackBarconfig({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      });
  };

  useEffect(() => {
    dispatch(getReferralDetails(`/${oppId}`));
  }, []);

  const handleAddMore = () => {
    let data = {
      fullName: "",
      email: "",
      countryCode: "IN",
      mobileNumber: "",
      opportunityId: oppId || "",
      primarySource:
        enquiryDetails?.data?.projectBrandName === "Provident"
          ? PRIMARY_SOURCE?.ProvidentPremiere
          : PRIMARY_SOURCE?.PurvaPrivilege,
      secondarySource: "Referral at booking",
      projectName: enquiryDetails?.data?.ProjectName || "",
      referredApartment: getBrandName() || "",
      projectCity: enquiryDetails?.data?.projectCity || "",
    };
    let updateArray = ReferralDetailFormik?.values?.referralList || [];
    updateArray.push(data);
    ReferralDetailFormik.setFieldValue("referralList", updateArray);
  };

  const handleRemoveMore = (index) => {
    let updateArray = ReferralDetailFormik?.values?.referralList || [];
    updateArray.splice(index, 1);
    ReferralDetailFormik.setFieldValue("referralList", updateArray);
  };

  const handleSnackbarClose = () => {
    setSnackBarconfig({
      open: false,
      message: "",
      severity: "",
    });
  };


  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <div>
        <Modal
          open={showReferralModal}
          onClose={() => {
            dispatch(setShowReferralModal(false));
            dispatch(setActiveStep(activeStep + 1));
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          className="ReferralDetailsModal"
        >
          <div className="modalContainer">
            <Box sx={{ border: "0" }}>
              <div
                className="closeModal"
                onClick={() => {
                  dispatch(setShowReferralModal(false));
                  dispatch(setActiveStep(activeStep + 1));
                }}
              >
                <HighlightOffIcon />
              </div>
              <Typography
                className="modalTitle"
                id="modal-modal-title"
                variant="h6"
                component="h2"
              >
                {t("referralDetails.title")}
              </Typography>
              <div className="titleDear">
                {t("referralDetails.dear", { name: userName })}
              </div>
              <Typography id="modal-modal-description" className="titlePra">
                {t("referralDetails.introText", { brandName, brandShortName })}
              </Typography>
            </Box>

            {referralDetails?.count > 0 ? (
              <>
                <Typography
                  sx={{
                    textAlign: "center",
                    mb: 2,
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                >
                  {t("referralDetails.alreadyReferredContacts")}
                </Typography>
                <div className="tbResponsiveReferral">
                  <table>
                    <tr>
                      <th>{t("commonLabels.nameLabel")}</th>
                      <th>{t("commonLabels.contactNumber")}</th>
                      <th>{t("commonLabels.email")}</th>
                    </tr>
                    {referralDetails?.referrals?.map((row) => (
                      <tr>
                        <td>{row.fullName}</td>
                        <td>{`${row.countryCode}${row.mobileNumber}`}</td>
                        <td>{row.email || "N/A"}</td>
                      </tr>
                    ))}
                  </table>
                </div>
              </>
            ) : null}

            {ReferralDetailFormik?.values?.referralList &&
              ReferralDetailFormik?.values?.referralList?.map((data, index) => {
                return (
                  <>
                   
                    {ReferralDetailFormik?.values?.referralList?.length +
                      referralDetails?.count <=
                      10 && (
                      <Grid key={index} container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4} lg={4} xl={4} sx={{
                            position: "relative"
                          }}>
                          <TextFieldInput
                            field={{
                              name: `referralList[${index}].fullName`,
                              label: t("commonLabels.nameLabel"),
                              placeholder: t("commonPlaceholders.name"),
                              required: true,
                              readonly: false,
                              listKey: "referralList",
                              index: index,
                              nameKey: "fullName",
                              maxLength: 50,
                            }}
                            formik={ReferralDetailFormik}
                          />
                             {index > 0 && (
                      <span className="removeReferral mobile">
                        <RemoveCircleIcon
                          sx={{
                            color: "#ff0000",
                          }}
                          onClick={() => handleRemoveMore(index)}
                        />
                      </span>
                    )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                          <RHFPhoneInput
                            name={`referralList.${index}.mobileNumber`}
                            countryCodeName={`referralList.${index}.countryCode`}
                            label={t("commonLabels.contactNumber")}
                            country={
                              ReferralDetailFormik?.values?.referralList?.[
                                index
                              ]?.countryCode ?? "IN"
                            }
                            formik={ReferralDetailFormik}
                            placeholder={t("commonPlaceholders.contactNumber")}
                            required
                            showDialCode={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={4} xl={4} sx={{
                            position: "relative"
                          }}>
                          <TextFieldInput
                            field={{
                              name: `referralList[${index}].email`,
                              label: t("commonLabels.email"),
                              required: false,
                              readonly: false,
                              placeholder: t("commonPlaceholders.emailAddress"),
                              listKey: "referralList",
                              index: index,
                              nameKey: "email",
                              maxLength: 50,
                            }}
                            formik={ReferralDetailFormik}
                          />
                      {index > 0 && (
                      <span className="removeReferral hideMobile">
                        <RemoveCircleIcon
                          sx={{
                            color: "#ff0000",
                          }}
                          onClick={() => handleRemoveMore(index)}
                        />
                      </span>
                    )}

                        </Grid>
                      </Grid>
                    )}
                  </>
                );
              })}

            {ReferralDetailFormik?.values?.referralList?.length +
              referralDetails?.count <
              10 && (
              <div className="addMoreContainer">
                <div
                  className={`addMoreBtn `}
                  // ${
                  //   !(
                  //     ReferralDetailFormik?.isValid &&
                  //     ReferralDetailFormik?.dirty
                  //   )
                  //     ? "cursorNotAllowed"
                  //     : ""
                  // }
                  // onClick={
                  //   ReferralDetailFormik?.isValid && ReferralDetailFormik?.dirty
                  //     ? handleAddMore
                  //     : null
                  // }
                  onClick={handleAddMore}
                >
                  <img alt="" src={AddIcon} />
                  <span>{t("referralDetails.addMore")}</span>
                </div>
              </div>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                width: "100%",
                mt: "20px",
                gap: 2,
              }}
            >
              <Button
                type="submit"
                sx={{
                  width: "118px",
                  height: "48px",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#1A407D",
                  fontSize: "16px",
                  fontWeight: "600",
                  lineHeight: "24px",
                  textTransform: "capitalize",
                  border: "1px solid #1A407D",
                  "&:hover": {
                    backgroundColor: "#092552",
                    color: "#fff",
                  },
                }}
                onClick={() => {
                  dispatch(setShowReferralModal(false));
                  dispatch(setActiveStep(activeStep + 1));
                }}
              >
                {t("referralDetails.skip")}
              </Button>
              <Button
                type="submit"
                className={
                  !(
                    ReferralDetailFormik?.isValid && ReferralDetailFormik?.dirty
                  )
                    ? "disableCTA"
                    : ""
                }
                sx={{
                  width: "157px",
                  height: "48px",
                  borderRadius: "8px",
                  background: "#1A407D",
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: "600",
                  lineHeight: "24px",
                  textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: "#092552",
                  },
                }}
                onClick={() => {
                  handleSaveForm();
                }}
                disabled={
                  !(
                    ReferralDetailFormik?.isValid && ReferralDetailFormik?.dirty
                  )
                }
              >
                {t("button.submit")}
              </Button>
            </Box>
          </div>
        </Modal>
      </div>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default ReferralDetails;
